import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import ProductCard from '../components/ProductCard';
import { reviews } from '../mock/mockData';
import {
  ShoppingCart,
  Heart,
  Star,
  Check,
  X,
  TruckIcon,
  ShieldCheck,
  ArrowLeft,
  Package
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { productAPI } from '../utils/api';

const ProductDetailPage = ({ onAddToCart }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await productAPI.getProduct(id);
      
      const transformedProduct = {
        ...response.data.product,
        inStock: response.data.product.in_stock !== undefined ? response.data.product.in_stock : true,
        stockQuantity: response.data.product.stock_quantity || 0,
        partNumber: response.data.product.part_number || '',
        originalPrice: response.data.product.original_price || null,
        weightKg: response.data.product.weight_kg || 0
      };
      
      setProduct(transformedProduct);

      const relatedRes = await productAPI.getProducts({
        category: response.data.product.category,
        limit: 4
      });
      
      const transformedRelated = relatedRes.data.products.map(product => ({
        ...product,
        inStock: product.in_stock !== undefined ? product.in_stock : true,
        stockQuantity: product.stock_quantity || 0,
        partNumber: product.part_number || '',
        originalPrice: product.original_price || null,
        weightKg: product.weight_kg || 0
      }));
      
      setRelatedProducts(transformedRelated.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error fetching product:', error);
      toast({
        title: t('common.error'),
        description: t('products.product_not_found'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('products.product_not_found')}</h2>
          <Button onClick={() => navigate('/products')}>{t('products.browse_products')}</Button>
        </div>
      </div>
    );
  }

  const productReviews = reviews.filter(r => r.productId === product.id);

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      onAddToCart(product);
    }
    toast({
      title: t('cart.add'),
      description: `${quantity}x ${product.name} ${t('products.add_to_cart')}`,
    });
  };

  const discount = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/products')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('products.back_to_products')}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Product Images */}
          <div>
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-4">
              <div className="relative" style={{ maxHeight: '480px' }}>
                <img
                  src={selectedImage || product.image}
                  alt={product.name}
                  className="w-full h-full object-contain"
                  style={{ maxHeight: '480px' }}
                />
                {discount > 0 && (
                  <Badge className="absolute top-4 left-4 bg-red-500 hover:bg-red-600 text-white font-bold text-lg px-3 py-1">
                    -{discount}%
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Image Thumbnails */}
            {product.images && product.images.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(img)}
                    className={`rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === img || (!selectedImage && index === 0)
                        ? 'border-orange-500 shadow-md'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-20 object-contain bg-white"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="mb-4">
                {(() => {
                  const sel = localStorage.getItem('fitmentSelection');
                  try {
                    const v = sel ? JSON.parse(sel) : null;
                    if (v && product.fitment && product.fitment.length > 0) {
                      const matched = product.fitment.some(f =>
                        f.region === v.region && f.make === v.make && f.model === v.model &&
                        parseInt(f.year) === parseInt(v.year) && f.trim === v.trim && f.engine === v.engine
                      );
                      if (matched) {
                        return (
                          <div className="mb-3">
                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              Guaranteed to fit • {v.year} {v.make} {v.model} • {v.trim} • {v.engine} • {v.region}
                            </span>
                          </div>
                        );
                      }
                    }
                  } catch(e) {}
                  return null;
                })()}

                <p className="text-sm font-semibold text-blue-600 mb-2">{product.brand}</p>
                <h1 className="text-2xl font-bold text-gray-900 mb-2 leading-snug">{product.name}</h1>
                <p className="text-sm text-gray-600">{t('products.sku')}: {product.partNumber}</p>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-5 w-5 ${
                        i < Math.floor(product.rating)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm text-gray-600">
                  {product.rating} ({product.reviews} {t('products.reviews')})
                </span>
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-3xl font-bold text-gray-900">
                    OMR {product.price.toFixed(2)}
                  </span>
                  {product.originalPrice && (
                    <span className="text-lg text-gray-500 line-through">
                      OMR {product.originalPrice.toFixed(2)}
                    </span>
                  )}
                </div>
                {discount > 0 && (
                  <p className="text-green-600 font-semibold">
                    {t('products.you_save', { amount: (product.originalPrice - product.price).toFixed(2), percent: discount })}
                  </p>
                )}
              </div>

              {/* Stock Status */}
              <div className="mb-6">
                {product.inStock ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <Check className="h-5 w-5" />
                    <span className="font-semibold">{t('products.in_stock')}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-600">
                    <X className="h-5 w-5" />
                    <span className="font-semibold">{t('products.out_of_stock')}</span>
                  </div>
                )}
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('products.quantity')}
                </label>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={!product.inStock}
                  >
                    -
                  </Button>
                  <span className="text-lg font-semibold w-12 text-center">{quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(quantity + 1)}
                    disabled={!product.inStock}
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mb-6">
                <Button
                  onClick={handleAddToCart}
                  disabled={!product.inStock}
                  className="flex-1 h-12 text-lg font-semibold bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  {t('products.add_to_cart')}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-12 w-12"
                >
                  <Heart className="h-5 w-5" />
                </Button>
              </div>

              {/* Features */}
              <div className="border-t pt-6 space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <TruckIcon className="h-5 w-5 text-green-600" />
                  <span className="text-gray-700">{t('products.free_delivery_info')}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <ShieldCheck className="h-5 w-5 text-blue-600" />
                  <span className="text-gray-700">2-year warranty included</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Package className="h-5 w-5 text-purple-600" />
                  <span className="text-gray-700">Easy returns within 30 days</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-12">
          <Tabs defaultValue="description" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="description">{t('products.description')}</TabsTrigger>
              <TabsTrigger value="specifications">{t('products.specifications')}</TabsTrigger>
              <TabsTrigger value="reviews">{t('products.reviews')} ({productReviews.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="description" className="mt-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('products.product_description')}</h3>
                <p className="text-gray-700 mb-4">{product.description}</p>
                <h4 className="font-semibold text-gray-900 mb-2">{t('products.compatible_vehicles')}</h4>
                <ul className="list-disc list-inside text-gray-700">
                  {product.compatibility.map((vehicle, index) => (
                    <li key={index}>{vehicle}</li>
                  ))}
                </ul>
              </div>
            </TabsContent>
            
            <TabsContent value="specifications" className="mt-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('products.technical_specifications')}</h3>
                <table className="w-full">
                  <tbody>
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <tr key={key} className="border-b">
                        <td className="py-3 font-medium text-gray-900">{key}</td>
                        <td className="py-3 text-gray-700">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
            
            <TabsContent value="reviews" className="mt-6">
              <div className="space-y-6">
                {productReviews.length > 0 ? (
                  productReviews.map((review) => (
                    <Card key={review.id}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold text-gray-900">{review.userName}</p>
                            <p className="text-sm text-gray-600">{review.date}</p>
                          </div>
                          {review.verified && (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                              {t('products.verified_purchase')}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center mb-3">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < review.rating
                                  ? 'text-yellow-400 fill-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-gray-700">{review.comment}</p>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <p className="text-gray-600 text-center py-8">No reviews yet. Be the first to review this product!</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('products.related_products')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard
                  key={relatedProduct.id}
                  product={relatedProduct}
                  onAddToCart={(p) => {
                    onAddToCart(p);
                    toast({
                      title: t('cart.add'),
                      description: `${p.name} ${t('products.add_to_cart')}`,
                    });
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
